import React, { useContext } from "react"
import moment from "moment"
import {
  DetailField,
  DetailListBadge,
  DetailListText,
  DetailListContainer,
  DetailButtonContainer,
  AddToFavoritesBtn,
  ConnectCard,
  TabContainer,
  DetailsTabGrid,
  QuotesTab,
  AboutReportTab,
  AboutReportTooltip,
} from "./components"
import { TabItem } from "./components/TabContainer"
import { DownloadIcon, EyeOpenIcon } from "./components/icons"
import { withRouter } from "react-router"
import { ImBubbles4, ImStack } from "react-icons/im"
import { RiCalendarCheckLine } from "react-icons/ri"
import { BsCalendar4Range } from "react-icons/bs"
import AppContext from "../../shared/AppContext"
import { canViewProduct } from "../../shared/helpers"
import PurchaseCard from "../../components/PurchaseCard"
import { Link } from "react-router-dom"
import Button from "../Button"

import "./style.scss"
import { SHARED_INSIGHTS_REPORT_TYPE } from "../../shared/constants"

const getFallbackImageSrc = (reportType) => {
  const baseStaticPath = "/static/images"

  if (
    !reportType ||
    !Object.keys(SHARED_INSIGHTS_REPORT_TYPE).includes(reportType)
  ) {
    return baseStaticPath + "/insights-fallback.svg"
  }

  return SHARED_INSIGHTS_REPORT_TYPE[reportType].defaultImage
}

const getReportTypeText = (reportType) => {
  if (
    !reportType ||
    !Object.keys(SHARED_INSIGHTS_REPORT_TYPE).includes(reportType)
  ) {
    return ""
  }

  return SHARED_INSIGHTS_REPORT_TYPE[reportType].displayName
}

const SharedInsightsProductDetails = (props) => {
  const { openInquireModal, serviceType, trackEvent } = useContext(AppContext)

  if (!props.data) {
    return null
  }

  const {
    data: { page: product },
    history,
    location,
    viewer,
  } = props
  const {
    id,
    name,
    logo,
    proprietaryDataset: reportType,
    vendor,
    sector,
    description,
    metadata,
    publishDate,
    files,
    isPurchased,
    free,
    available,
  } = product

  const isDownloadButtonVisible = () => {
    if (!viewer || !viewer.isUserAuthenticated) return false
    if (!files || !files.length) return false

    if (product.isPurchased || product.free) {
      return true
    }

    if (viewer.isFreeTrial) return false

    return product.isSpecialAccess
  }

  const handleImageError = (e) => {
    e.target.src = getFallbackImageSrc(reportType)
  }

  return (
    <div className="shared-insights-main">
      {free && (
        <div className="free-ribbon">
          <span>Free Report!</span>
        </div>
      )}
      <AboutReportTooltip reportType={reportType} id="report-type" />
      <div className="shared-insights-details-card">
        <div className="shared-insights-product-title-section">
          <h1>{name}</h1>
        </div>
        <div className="shared-insights-detail-container">
          <div className="shared-insights-logo-section">
            <div className="shared-insights-logo-container">
              <img
                onError={handleImageError}
                src={logo || getFallbackImageSrc(reportType)}
                alt="product image"
              />
            </div>
          </div>
          <div className="shared-insights-detail-field-container">
            <DetailField label="Report:">
              <DetailListText>
                <span
                  data-tip
                  data-for="report-type"
                  style={{ cursor: "help" }}
                >
                  {getReportTypeText(reportType)}
                </span>
              </DetailListText>
            </DetailField>
            {vendor && !!vendor.length && (
              <DetailField label="Vendors:">
                <DetailListContainer>
                  {vendor.map((v, idx) => (
                    <DetailListText
                      key={v.name}
                      index={idx}
                      length={vendor.length}
                    >
                      <Link to={`/vendor/${v.slug}`}>{v.name}</Link>
                    </DetailListText>
                  ))}
                </DetailListContainer>
              </DetailField>
            )}
            {sector && !!sector.length && (
              <DetailField label="Sectors:">
                <DetailListContainer>
                  {sector.map((s, idx) => (
                    <DetailListText
                      key={s.name}
                      index={idx}
                      length={sector.length}
                    >
                      <Link to={`/sector/${s.slug}`}>{s.name}</Link>
                    </DetailListText>
                  ))}
                </DetailListContainer>
              </DetailField>
            )}
            {metadata.tags && !!metadata.tags.length && (
              <DetailField label="In this Report:">
                <DetailListContainer>
                  {metadata.tags.map((t) => (
                    <DetailListBadge key={t}>{t}</DetailListBadge>
                  ))}
                </DetailListContainer>
              </DetailField>
            )}
            <DetailField label="Description:" style={{ display: "block" }}>
              <div style={{ maxWidth: 1000, marginBottom: 15 }}>
                {description}
              </div>
            </DetailField>
            <DetailButtonContainer>
              {canViewProduct(product) && (
                <Button
                  data-cy="view-product-button"
                  className="shared-insights-primary-btn"
                  classes={["dark-fill", "small-caps"]}
                  icon={<EyeOpenIcon />}
                  action={() => {
                    trackEvent("Insights Report Page: view button click", {
                      productId: id,
                      productName: name,
                      productType: reportType,
                    })
                    history.push(`${location.pathname}/view`)
                  }}
                >
                  VIEW
                </Button>
              )}

              {isDownloadButtonVisible() ? (
                <Button
                  data-cy="download-product-button"
                  className="shared-insights-primary-btn download"
                  classes={["green-fill", "small-caps"]}
                  icon={<DownloadIcon />}
                  download={files[0].downloadUrl}
                  disabled={!files[0].downloadUrl}
                  action={() => {
                    trackEvent("Insights Report Page: download button click", {
                      productId: id,
                      productName: name,
                      productType: reportType,
                    })
                  }}
                >
                  DOWNLOAD
                  <span
                    className="glyphicon glyphicon-download-alt"
                    style={{ marginLeft: 5 }}
                  />
                </Button>
              ) : null}
              {!free &&
              !isPurchased &&
              !product.isSpecialAccess &&
              available ? (
                <PurchaseCard
                  subscription
                  recommended
                  description=""
                  buttonText="INQUIRE FOR PRICING"
                  buttonAction={() => {
                    openInquireModal(
                      serviceType.UpgradePlan,
                      "Insights Report Page: inquire for pricing button click"
                    )
                  }}
                  data-cy="purchase-card-inquire-button"
                />
              ) : null}
              <AddToFavoritesBtn product={product} />
            </DetailButtonContainer>
          </div>
        </div>
      </div>
      <ConnectCard
        onClick={() => {
          openInquireModal(
            serviceType.TalkToSurveyRespondents,
            "Insights Report Page: ETR connect get started button click"
          )
        }}
      />
      <TabContainer product={product}>
        <TabItem label="Details">
          <DetailsTabGrid>
            {metadata["Events Count"] &&
            parseInt(metadata["Events Count"]) > 0 ? (
              <div className="grid-item">
                <div className="grid-item-detail-label">Events Cited</div>
                <ImBubbles4 fontSize={35} color="#325f7d" />
                <div className="grid-item-detail-value">
                  {metadata["Events Count"]}
                </div>
              </div>
            ) : null}
            {metadata["Page Count"] && parseInt(metadata["Page Count"]) > 0 ? (
              <div className="grid-item">
                <div className="grid-item-detail-label">Pages in Report</div>
                <ImStack fontSize={35} color="#325f7d" />
                <div className="grid-item-detail-value">
                  {metadata["Page Count"]}
                </div>
              </div>
            ) : null}
            {publishDate ? (
              <div className="grid-item">
                <div className="grid-item-detail-label">Report Publication</div>
                <RiCalendarCheckLine fontSize={35} color="#325f7d" />
                <div className="grid-item-detail-value">
                  {moment(publishDate).format("MMM Do YYYY")}
                </div>
              </div>
            ) : null}
            {metadata["Events Start Date"] && metadata["Events End Date"] ? (
              <div className="grid-item">
                <div className="grid-item-detail-label">Report Time Frame</div>
                <BsCalendar4Range fontSize={35} color="#325f7d" />
                <div className="grid-item-detail-value">{`${moment(
                  metadata["Events Start Date"],
                  "MM/DD/YYYY"
                ).format("MMM Do YYYY")} - ${moment(
                  metadata["Events End Date"],
                  "MM/DD/YYYY"
                ).format("MMM Do YYYY")}`}</div>
              </div>
            ) : null}
          </DetailsTabGrid>
        </TabItem>
        {metadata["Teaser Quotes"] && metadata["Teaser Quotes"].length ? (
          <TabItem label="Quotes">
            <QuotesTab quotes={metadata["Teaser Quotes"]} />
          </TabItem>
        ) : null}
        <TabItem
          label={
            SHARED_INSIGHTS_REPORT_TYPE[reportType]
              ? `About ${SHARED_INSIGHTS_REPORT_TYPE[reportType].displayName}`
              : "About"
          }
        >
          <AboutReportTab reportType={reportType} />
        </TabItem>
      </TabContainer>
    </div>
  )
}

export default withRouter(SharedInsightsProductDetails)
